import { Injectable } from '@angular/core';
import { AngularFireFunctions } from 'angularfire2/functions';
import * as firebase from 'firebase/app';
import 'firebase/storage';
import algoliasearch from 'algoliasearch';
import { algoliaConfig } from 'src/environments/environment.prod';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import {
    CopyExistingPolicy, CopyPlanItem, ExistingProduct, ExtractedProduct, ImportZone, PreparedRow, ZoneCatalog,
    isNewZoneKey, joinZonePath, newZoneNames, normalizeText, planCopy as planCopyPure, splitZonePath,
} from 'src/app/shared/product-import/product-import.model';
import { cleanSectionName, mergeSectionNames } from 'src/app/shared/event-products/product-sections';
import { parseVariants } from 'src/app/shared/event-products/product-variants';
import {
    EventInventory, InventoryEdit, InventoryPresentation, InventoryProduct, InventoryTransferLog, InventoryTransferResult, InventoryZone,
    matchPresentation,
} from 'src/app/shared/event-inventory/event-inventory.model';
import { formatDate } from 'src/app/shared/event-report/event-report.model';

/** Imagen con la que nacen los productos sin foto (la misma que usa addBrick). */
export const DEFAULT_BRICK_IMAGE =
    'https://firebasestorage.googleapis.com/v0/b/barlleno.appspot.com/o/public%2FiconBotella2021.png?alt=media&token=3f057adb-1ae2-4fdc-b3ff-a9ef6ab94dd1';

/** Imagen de las categorías (zonas) nuevas: la misma ruta relativa que usa la página de zona. */
export const DEFAULT_CATEGORY_IMAGE = '../../assets/icons/iconBotella2021.png';

export type ImportSource = 'xlsx' | 'csv' | 'pdf' | 'image';

export interface ImportContext {
    eventKey: string;
    source: ImportSource;
    fileName: string;
    /** Zona desde la que se importa: dentro de ella se crean las ubicaciones nuevas. */
    zone?: ImportZone;
}

export interface ImportResult {
    created: number;
    updated: number;
    /** Categorías creadas para ubicaciones que no existían. */
    zones?: number;
}

export { CopyExistingPolicy, CopyPlanItem, ZoneCatalog };

export interface CopyOptions {
    sourceZone: ImportZone;
    eventKey: string;
    /** false: las copias nacen con inventario 0 y ocultas. */
    copyInventory: boolean;
}

export interface CopyResult {
    created: number;
    updated: number;
    skipped: number;
}

/** Una presentación tal como la escribe el formulario de producto de evento. */
export interface NewEventPresentation {
    /** Etiqueta completa: "Botella 750ML", "Vaso", "Unidad". */
    name: string;
    price: number;
    inventory: number;
    visible: boolean;
    main: boolean;
}

/** Lo que pide el formulario de alta de producto en una barra. */
export interface NewEventProduct {
    zoneKey: string;
    eventKey: string;
    name: string;
    supplier: string;
    code: string;
    description: string;
    /** Sección con la que la caja agrupa ("Botellas", "Cócteles"…); vacío si no aplica. */
    section: string;
    /** Se prepara en cocina: sus órdenes salen en la pantalla de cocina (KDS). */
    kitchen: boolean;
    /** Alternativas del mismo producto ("Whiskey" en un Squeeze); vacío si no tiene. Ver shared/event-products/product-variants. */
    variants: string[];
    visible: boolean;
    position: number;
    presentations: NewEventPresentation[];
    /** URL de una imagen que ya existe (tomada de otro producto); vacío si no hay o si se sube archivo. */
    imageUrl: string;
    /** $key del producto del que se tomó la imagen, para saber de dónde vino. */
    imageFrom: string;
}

export interface CreatedEventProduct {
    key: string;
    name: string;
    imageUrl: string;
}

/** Presentación al editar: con `key` si ya existe en Firestore, vacía si es nueva. */
export interface EditEventPresentation extends NewEventPresentation {
    key: string;
}

/** Lo que pide el formulario al editar un producto existente. */
export interface EditEventProduct {
    key: string;
    zoneKey: string;
    name: string;
    supplier: string;
    code: string;
    description: string;
    section: string;
    kitchen: boolean;
    variants: string[];
    visible: boolean;
    presentations: EditEventPresentation[];
    /** Presentaciones que se quitaron en el formulario. */
    removedPresentationKeys: string[];
    /** Qué hacer con la foto: dejarla, subir archivo, reutilizar una URL o volver a la genérica. */
    image: 'keep' | 'upload' | 'pick' | 'clear';
    imageUrl: string;
    imageFrom: string;
}

/** Una imagen ya usada por productos, para reutilizarla en uno nuevo. */
export interface ProductImageOption {
    url: string;
    /** Producto del que se muestra (el primero que la usa). */
    key: string;
    name: string;
    /** Todos los productos que la usan, sin repetir nombre. */
    names: string[];
    count: number;
}

/** Un producto (o referencia) tal como vive en una zona, para la auditoría. */
export interface AuditEntry {
    key: string;
    name: string;
    /** Zona de primer nivel a la que pertenece (las sub-zonas cuentan para su zona madre). */
    zoneKey: string;
    hidden: boolean;
    position: number;
    isReference: boolean;
    /** Producto original cuando es referencia. */
    brick: string;
}

export interface EventCatalog {
    zones: ImportZone[];
    entries: AuditEntry[];
}

/** Tipos de archivo que la extracción con IA acepta (PDF y capturas). */
export const EXTRACTABLE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];
/** Límite del archivo para la función (el payload de una callable es de 10 MB en base64). */
export const EXTRACT_MAX_BYTES = 7 * 1024 * 1024;

/**
 * Operaciones masivas sobre los productos de una zona de evento: importar
 * desde un archivo, vaciar la zona y consultar lo necesario para ambas.
 * La lectura de archivos y la validación viven en shared/product-import;
 * aquí solo se habla con Firestore y con la función de extracción.
 */
@Injectable({
    providedIn: 'root'
})
export class EventProductsService {

    /** Filas por lote: cada fila escribe hasta 3 documentos y el batch admite 500. */
    private readonly ROWS_PER_BATCH = 150;
    private readonly DELETES_PER_BATCH = 400;
    /** Copias: se cierra el lote al pasar de aquí (un producto son 1 + N presentaciones). */
    private readonly OPS_PER_BATCH = 400;

    constructor(
        private api: ApiService,
        private auth: AuthService,
        private fireFunctions: AngularFireFunctions,
    ) { }

    // ═══════════════════════════════════════════════════════════════════════
    //  CONSULTAS
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Zonas del evento que aceptan productos (las de primer nivel), para
     * resolver la columna "Ubicación". La zona actual siempre va incluida
     * aunque la consulta no la traiga.
     */
    /**
     * Zonas a las que puede ir un producto importado desde `current`: las de
     * primer nivel, la actual y todas las categorías que cuelgan de la actual
     * (a cualquier profundidad) con su ruta, para que "Precio Regular /
     * Botellas" se reconozca y no se duplique al reimportar.
     */
    async getEventZones(eventKey: string, current: ImportZone): Promise<ImportZone[]> {
        const zones: ImportZone[] = [];
        const all: { key: string; name: string; parent: string; main: boolean }[] = [];
        if (eventKey) {
            const snap = await this.api.getRef('bricks').ref
                .where('event_key', '==', eventKey)
                .where('type', '==', 'category')
                .get();
            snap.forEach(doc => {
                const data = doc.data();
                if (data.delete === true || data.main_event === true) return;
                all.push({ key: doc.id, name: String(data.name || ''), parent: String(data.category || ''), main: data.subCategoryMain === true });
            });
        }
        all.forEach(z => {
            if (z.main || (current && z.key === current.key)) zones.push({ key: z.key, name: z.name });
        });
        if (current && current.key) {
            this.descendantsOf(all, current.key, []).forEach(z => zones.push(z));
            if (!zones.some(z => z.key === current.key)) zones.unshift(current);
        }
        return zones.sort((a, b) => (a.key === current.key ? -1 : b.key === current.key ? 1 : (a.path || a.name).localeCompare(b.path || b.name)));
    }

    /** Categorías bajo `parentKey`, en orden de profundidad, con la ruta de nombres desde el padre. */
    private descendantsOf(all: { key: string; name: string; parent: string }[], parentKey: string, prefix: string[], depth: number = 0): ImportZone[] {
        if (depth > 6) return [];
        const out: ImportZone[] = [];
        all.filter(z => z.parent === parentKey).forEach(z => {
            const parts = prefix.concat([z.name]);
            out.push({ key: z.key, name: z.name, path: joinZonePath(parts) });
            this.descendantsOf(all, z.key, parts, depth + 1).forEach(d => out.push(d));
        });
        return out;
    }

    /**
     * Categorías que cuelgan de una zona, para la página de zona. Se leen de
     * Firestore y no de la API de tienda: esa API filtra por storeKey y a un
     * usuario de otra tienda con el evento asignado le devolvía la zona vacía.
     */
    async getZoneCategories(zoneKey: string): Promise<any[]> {
        const snap = await this.api.getRef('bricks').ref
            .where('category', '==', zoneKey)
            .where('type', '==', 'category')
            .get();
        const list: any[] = [];
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            data.$key = doc.id;
            const created = this.toDate(data.created_at);
            data.date = created ? formatDate(created) : '';
            list.push(data);
        });
        return list.sort((a, b) => Number(a.position || 0) - Number(b.position || 0)
            || String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }));
    }

    /**
     * Productos de una zona tal como los lista su página: precio, inventario
     * y oferta de la presentación principal, código y, para las referencias,
     * el producto original con `reference` apuntando al documento de la
     * referencia (misma forma que devolvía la API de tienda).
     */
    async getZoneProductRows(zoneKey: string): Promise<any[]> {
        const snap = await this.api.getRef('bricks').ref
            .where('category', '==', zoneKey)
            .get();
        const docs: any[] = [];
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            if (data.type !== 'brick' && data.type !== 'club' && data.type !== 'reference') return;
            data.$key = doc.id;
            docs.push(data);
        });

        const rows = await Promise.all(docs.map(async data => {
            if (data.type === 'reference') {
                let original: any = null;
                try {
                    original = data.brick ? await this.api.getDocument('bricks', data.brick) : null;
                } catch (e) {
                    original = null;
                }
                if (!original || original.delete === true) return null;
                const product: any = { ...original, $key: data.brick, reference: { ...data } };
                // La referencia se ordena donde está en esta zona, no donde está el original.
                if (data.position !== undefined) product.position = data.position;
                await this.attachMainPresentation(product);
                return product;
            }
            const product: any = { ...data };
            await this.attachMainPresentation(product);
            return product;
        }));

        return rows.filter(r => !!r).sort((a, b) => Number(a.position || 0) - Number(b.position || 0)
            || String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }));
    }

    /** Precio, inventario, oferta y código del producto a partir de su presentación principal. */
    private async attachMainPresentation(product: any) {
        let presentations: any[] = [];
        try {
            presentations = (await this.api.getAllDocuments(`bricks/${product.$key}/presentations`)) || [];
        } catch (e) {
            presentations = [];
        }
        const main = presentations.filter(p => p && p.main === true)[0] || presentations[0] || null;
        product.presentations = presentations;
        product.price = main ? Number(main.price || 0) : 0;
        product.inventory = main ? Number(main.inventory || 0) : 0;
        const undiscounted = main && main.undiscounted_price !== undefined && main.undiscounted_price !== null
            ? Number(main.undiscounted_price) : null;
        product.priceUndiscount = undiscounted !== null && isFinite(undiscounted) && undiscounted > product.price ? undiscounted : null;
        product.productCode = String(product.system_code || (Array.isArray(product.bar_code) ? product.bar_code[0] : '') || '').trim();
    }

    private toDate(value: any): Date | null {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value.toDate === 'function') return value.toDate();
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    /** Productos propios (no referencias) vivos en una zona, para no duplicarlos. */
    async getZoneProducts(zoneKey: string): Promise<ExistingProduct[]> {
        const snap = await this.api.getRef('bricks').ref
            .where('category', '==', zoneKey)
            .where('type', '==', 'brick')
            .get();
        const list: ExistingProduct[] = [];
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            list.push({ key: doc.id, name: String(data.name || '') });
        });
        return list;
    }

    /** Productos y referencias vivos de una zona (las sub-zonas no cuentan). */
    async getZoneCatalog(zoneKey: string): Promise<ZoneCatalog> {
        const snap = await this.api.getRef('bricks').ref
            .where('category', '==', zoneKey)
            .get();
        const catalog: ZoneCatalog = { bricks: [], references: [], count: 0 };
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            if (data.type === 'brick' || data.type === 'club') {
                catalog.bricks.push({ key: doc.id, name: String(data.name || '') });
                catalog.count += 1;
            } else if (data.type === 'reference') {
                catalog.references.push({ key: doc.id, brick: String(data.brick || '') });
                catalog.count += 1;
            }
        });
        return catalog;
    }

    /**
     * Todo el evento en una consulta: zonas de primer nivel y cada producto o
     * referencia viva con la zona a la que pertenece (lo que cuelga de una
     * sub-zona se atribuye a su zona madre, que es lo que ve la caja). Es la
     * base de la comparación entre zonas.
     */
    async getEventCatalog(eventKey: string): Promise<EventCatalog> {
        const result: EventCatalog = { zones: [], entries: [] };
        if (!eventKey) return result;
        const snap = await this.api.getRef('bricks').ref.where('event_key', '==', eventKey).get();

        const docs: { [key: string]: any } = {};
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            docs[doc.id] = { ...data, $key: doc.id };
        });

        const { zones, zoneOf } = this.zoneResolver(docs);
        result.zones = zones;

        // Nombres de referencias: el producto original puede estar fuera del evento.
        const missingNames: string[] = [];
        Object.keys(docs).forEach(k => {
            const d = docs[k];
            if (d.type === 'reference' && d.brick && !docs[d.brick]) missingNames.push(d.brick);
        });
        const external: { [key: string]: string } = {};
        for (const brickKey of missingNames.filter((k, i, all) => all.indexOf(k) === i)) {
            try {
                const doc = await this.api.getRef('bricks').doc(brickKey).ref.get();
                if (doc.exists) external[brickKey] = String(doc.data().name || '');
            } catch (e) { /* sin nombre: se muestra la llave */ }
        }

        Object.keys(docs).forEach(k => {
            const d = docs[k];
            if (d.type === 'brick' || d.type === 'club') {
                result.entries.push({
                    key: k, name: String(d.name || ''), zoneKey: zoneOf(d.category), hidden: d.visible === false,
                    position: Number(d.position) || 0, isReference: false, brick: k,
                });
            } else if (d.type === 'reference' && d.brick) {
                const original = docs[d.brick];
                result.entries.push({
                    key: k, name: original ? String(original.name || '') : (external[d.brick] || d.brick),
                    zoneKey: zoneOf(d.category), hidden: d.visible === false,
                    position: Number(d.position) || 0, isReference: true, brick: d.brick,
                });
            }
        });
        return result;
    }

    /**
     * Zonas de primer nivel de un evento (subCategoryMain, o hijas directas
     * de la raíz) y la función que sube por `category` desde cualquier
     * categoría hasta su zona de primer nivel (o se queda donde está).
     */
    private zoneResolver(docs: { [key: string]: any }): { zones: InventoryZone[]; zoneOf: (categoryKey: string) => string } {
        const rootKeys = Object.keys(docs).filter(k => docs[k].type === 'category' && docs[k].main_event === true);
        const topLevel: { [key: string]: boolean } = {};
        Object.keys(docs).forEach(k => {
            const d = docs[k];
            if (d.type !== 'category' || d.main_event === true) return;
            if (d.subCategoryMain === true || rootKeys.indexOf(d.category) >= 0) topLevel[k] = true;
        });
        const zoneOf = (categoryKey: string): string => {
            let key = categoryKey;
            for (let i = 0; i < 10 && key; i++) {
                if (topLevel[key]) return key;
                const parent = docs[key];
                if (!parent || parent.type !== 'category') break;
                key = parent.category;
            }
            return categoryKey;
        };
        const zones = Object.keys(topLevel)
            .map(k => ({ key: k, name: String(docs[k].name || '') }))
            .sort((a, b) => (Number(docs[a.key].position) || 0) - (Number(docs[b.key].position) || 0) || a.name.localeCompare(b.name));
        return { zones, zoneOf };
    }

    /**
     * Borrado lógico de productos concretos (misma marca que el borrado
     * individual): para repetidos dentro de una zona. Una referencia solo se
     * quita de la zona; un producto propio arrastra sus referencias.
     */
    async deleteEntries(entries: AuditEntry[], reason: string): Promise<number> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const mark = {
            delete: true,
            deleteDetails: { deletedBy: this.auth.token, deletedUser: this.auth.email || '', date: now, reason },
            visible: false,
        };
        const keys: string[] = [];
        for (const entry of entries) {
            keys.push(entry.key);
            if (entry.isReference) continue;
            const refs = await this.api.getRef('bricks').ref.where('brick', '==', entry.key).where('type', '==', 'reference').get();
            refs.forEach(doc => { if (doc.data().delete !== true) keys.push(doc.id); });
        }
        const unique = keys.filter((k, i) => keys.indexOf(k) === i);
        for (let i = 0; i < unique.length; i += this.DELETES_PER_BATCH) {
            const batch = firestore.batch();
            unique.slice(i, i + this.DELETES_PER_BATCH).forEach(key => batch.update(firestore.collection('bricks').doc(key), mark));
            await batch.commit();
        }
        return unique.length;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  EXTRACCIÓN CON IA (PDF y capturas)
    // ═══════════════════════════════════════════════════════════════════════

    private readAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = String(reader.result || '');
                resolve(result.substring(result.indexOf(',') + 1));
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    static mimeOf(file: File): string {
        const type = String(file.type || '').toLowerCase();
        if (type === 'image/jpg') return 'image/jpeg';
        if (type) return type;
        const name = String(file.name || '').toLowerCase();
        if (/\.pdf$/.test(name)) return 'application/pdf';
        if (/\.png$/.test(name)) return 'image/png';
        if (/\.jpe?g$/.test(name)) return 'image/jpeg';
        if (/\.webp$/.test(name)) return 'image/webp';
        if (/\.gif$/.test(name)) return 'image/gif';
        return '';
    }

    /**
     * Manda el archivo a la función `extractProductsFromFile`, que lo lee con
     * Claude y devuelve los productos ya estructurados. Se le pasan las zonas
     * del evento para que la columna de ubicación venga con el nombre exacto.
     */
    async extractFromFile(file: File, zones: ImportZone[]): Promise<{ products: ExtractedProduct[]; notes: string }> {
        const mimeType = EventProductsService.mimeOf(file);
        if (EXTRACTABLE_TYPES.indexOf(mimeType) < 0) {
            throw new Error('Solo se pueden leer PDF e imágenes PNG, JPG o WEBP.');
        }
        if (file.size > EXTRACT_MAX_BYTES) {
            throw new Error('El archivo pesa más de 7 MB. Recórtalo o divídelo en partes.');
        }
        const data = await this.readAsBase64(file);
        const extract = this.fireFunctions.httpsCallable('extractProductsFromFile');
        const response: any = await extract({
            countryCode: this.auth.countryCode,
            fileName: file.name,
            mimeType,
            data,
            zones: zones.map(z => z.name),
            currency: this.auth.currency || '',
        }).toPromise();
        return {
            products: response && Array.isArray(response.products) ? response.products : [],
            notes: response && response.notes ? String(response.notes) : '',
        };
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  IMPORTAR
    // ═══════════════════════════════════════════════════════════════════════

    private newBrick(row: PreparedRow, position: number, ctx: ImportContext, now: Date): any {
        const brick: any = {
            name: row.name,
            brand: row.supplier || 'Evento',
            line: 'Evento',
            description: ' ',
            matriz: row.name,
            visible: row.visible,
            category: row.zoneKey,
            type: 'brick',
            free_delivery: false,
            on_trade: false,
            exclusiveIcon: false,
            url_link: '',
            supplier: row.supplier || '',
            section: row.section || '',
            nivel: '',
            system_code: row.code || '',
            position,
            countryCode: this.auth.countryCode,
            storeCode: this.auth.storeCode,
            storeKey: this.auth.storeKey,
            boolAlcohol: false,
            detalles: {
                boolRegion: false, boolTemperatura: false, boolMaridaje: false, boolCristaleria: false, boolNotasCata: false,
            },
            activarVino: false,
            evento: true,
            event_key: ctx.eventKey,
            multiple_monitor: true,
            image: { downloadURL: DEFAULT_BRICK_IMAGE },
            created_at: now,
            account_email_create: this.auth.email || '',
            imported_at: now,
            imported_by: this.auth.email || '',
            import_source: ctx.source,
            import_file: ctx.fileName || '',
        };
        if (row.code) brick.bar_code = [row.code];
        return brick;
    }

    private newPresentation(row: PreparedRow): any {
        const presentation: any = {
            presentation: 'Unidad',
            inventory: row.inventory,
            price: row.price,
            bar_code: row.code || '',
            visible: row.visible,
            main: true,
        };
        if (this.auth.countryCode === 'DO') {
            presentation.inventory_ZONE1 = row.inventory;
            presentation.inventory_ZONE2 = 0;
        }
        return presentation;
    }

    /**
     * Crea categorías (sub-zonas) dentro de una zona del evento, igual que el
     * botón "agregar categoría" de la página de zona: el brick de la categoría
     * y su nodo en el árbol `zone_event` del evento, para que la caja con
     * zona fija incluya sus productos. Las rutas ("Precio Regular / Botellas")
     * se crean tramo por tramo, reutilizando los tramos que ya existen.
     * Devuelve ruta normalizada → llave de la categoría final, y cuántas
     * categorías se escribieron (los tramos reutilizados no cuentan).
     */
    async createCategories(eventKey: string, parent: ImportZone, paths: string[], now: Date = new Date()): Promise<{ byPath: { [path: string]: string }; created: number }> {
        const created: { [path: string]: string } = {};
        if (!paths || paths.length === 0) return { byPath: created, created: 0 };

        // El árbol se modifica y se vuelve a escribir: siempre la versión del
        // servidor, nunca la copia corta en caché (otra importación pudo cambiarlo).
        this.api.forgetDocument('events', eventKey);
        const event: any = await this.api.getDocument('events', eventKey);
        const isMain = !!event && event.brick_key === parent.key;
        const tree: any[] = event && Array.isArray(event.zone_event) ? event.zone_event : [];
        const firestore = this.api.db.firestore;
        const batch = firestore.batch();

        // Lo que ya cuelga de la zona, por ruta, para no duplicar tramos.
        const byPath: { [path: string]: ImportZone } = {};
        (await this.getEventZones(eventKey, parent)).forEach(z => { if (z.path) byPath[normalizeText(z.path)] = z; });
        /** Categorías creadas en esta corrida: ya nacen como carpeta, no se vuelven a tocar en el lote. */
        const createdKeys: { [key: string]: boolean } = {};
        /** Zonas que ya existían y reciben categorías: pasan a carpeta. */
        const existingParents: { [key: string]: boolean } = {};

        paths.forEach(path => {
            const parts = splitZonePath(path);
            let holder: ImportZone = parent;
            let holderIsMain = isMain;
            const prefix: string[] = [];
            parts.forEach(part => {
                prefix.push(part);
                const fullPath = normalizeText(joinZonePath(prefix));
                const found = byPath[fullPath];
                if (found) {
                    holder = found;
                    holderIsMain = false;
                    return;
                }
                const key = this.api.db.createId();
                createdKeys[key] = true;
                batch.set(firestore.collection('bricks').doc(key), {
                    $key: key,
                    name: part,
                    created_at: now,
                    type: 'category',
                    category: holder.key,
                    countryCode: this.auth.countryCode,
                    storeCode: this.auth.storeCode,
                    storeKey: this.auth.storeKey,
                    visible: true,
                    position: 0,
                    subCategory: holderIsMain,
                    subCategoryMain: holderIsMain,
                    evento: true,
                    event_key: eventKey,
                    image_logo: DEFAULT_CATEGORY_IMAGE,
                    multiple_monitor: true,
                    logo: true,
                    account_email_create: this.auth.email || '',
                });
                const node = {
                    $key: key, name: part, logo_zone: DEFAULT_CATEGORY_IMAGE, position: 0, code: '',
                    get_number: true, get_subZone: true, min_value: 1, value_max: 1000,
                };
                if (holderIsMain) {
                    tree.push(node);
                } else if (!this.insertSubZone(tree, holder.key, node)) {
                    // La zona no está en el árbol del evento (dato viejo o inconsistente): la
                    // categoría existe igual para la configuración, pero la caja con zona
                    // fija no la verá hasta que se corrija el árbol.
                    console.warn(`La zona ${holder.name} (${holder.key}) no está en zone_event: "${part}" queda fuera del árbol.`);
                }
                // Una zona con categorías es una carpeta (igual que addCategory).
                if (!createdKeys[holder.key]) existingParents[holder.key] = true;
                const zone: ImportZone = { key, name: part, path: joinZonePath(prefix) };
                byPath[fullPath] = zone;
                holder = zone;
                holderIsMain = false;
            });
            created[normalizeText(joinZonePath(parts))] = holder.key;
        });

        Object.keys(existingParents).forEach(key => {
            batch.update(firestore.collection('bricks').doc(key), { logo: true, event_key: eventKey });
        });
        batch.update(firestore.collection('events').doc(eventKey), { zone_event: tree });
        await batch.commit();
        this.api.forgetDocument('events', eventKey);
        return { byPath: created, created: Object.keys(createdKeys).length };
    }

    /** Cuelga `node` de la zona `parentKey` dentro del árbol, a cualquier profundidad. */
    private insertSubZone(tree: any[], parentKey: string, node: any): boolean {
        for (const zone of tree || []) {
            if (!zone) continue;
            if (zone.$key === parentKey) {
                if (!Array.isArray(zone.sub_zone)) zone.sub_zone = [];
                zone.sub_zone.push(node);
                return true;
            }
            if (Array.isArray(zone.sub_zone) && this.insertSubZone(zone.sub_zone, parentKey, node)) return true;
        }
        return false;
    }

    /** Presentación principal (o la primera) de un producto existente. */
    private async mainPresentationKey(brickKey: string): Promise<string> {
        const snap = await this.api.getRef(`bricks/${brickKey}/presentations`).ref.get();
        let first = '';
        let main = '';
        snap.forEach(doc => {
            if (!first) first = doc.id;
            if (!main && doc.data().main === true) main = doc.id;
        });
        return main || first;
    }

    /**
     * Escribe las filas preparadas en lotes: crea producto + presentación
     * principal, o actualiza precio/inventario/código/proveedor de uno que ya
     * existía. `positions` trae cuántos productos tiene cada zona destino para
     * que los nuevos queden al final del orden.
     */
    async importProducts(
        prepared: PreparedRow[],
        ctx: ImportContext,
        positions: { [zoneKey: string]: number },
        onProgress?: (done: number, total: number) => void,
    ): Promise<ImportResult> {
        const rows = prepared.filter(p => p.action === 'create' || p.action === 'update');
        const firestore = this.api.db.firestore;
        const now = new Date();
        const by = this.auth.email || '';
        const nextPosition = { ...positions };
        const touchedZones: { [zoneKey: string]: boolean } = {};
        const result: ImportResult = { created: 0, updated: 0, zones: 0 };
        let done = 0;

        // Ubicaciones que no existen: primero se crean como categorías dentro
        // de la zona actual y cada fila recibe la llave real.
        const missing = newZoneNames(rows);
        if (missing.length > 0) {
            if (!ctx.zone || !ctx.zone.key) throw new Error('Falta la zona desde la que se importa para crear las categorías nuevas.');
            const categories = await this.createCategories(ctx.eventKey, ctx.zone, missing, now);
            rows.forEach(row => {
                if (!row.newZone || !isNewZoneKey(row.zoneKey)) return;
                const key = categories.byPath[normalizeText(row.zonePath || row.zoneName)];
                if (!key) throw new Error(`No se pudo crear la categoría "${row.zonePath || row.zoneName}".`);
                row.zoneKey = key;
                row.newZone = false;
            });
            result.zones = categories.created;
        }

        for (let i = 0; i < rows.length; i += this.ROWS_PER_BATCH) {
            const chunk = rows.slice(i, i + this.ROWS_PER_BATCH);
            const batch = firestore.batch();

            for (const row of chunk) {
                if (row.action === 'create') {
                    const brickKey = this.api.db.createId();
                    const position = nextPosition[row.zoneKey] || 0;
                    nextPosition[row.zoneKey] = position + 1;
                    const brick = this.newBrick(row, position, ctx, now);
                    brick.$key = brickKey;
                    batch.set(firestore.collection('bricks').doc(brickKey), brick);
                    batch.set(
                        firestore.collection(`bricks/${brickKey}/presentations`).doc(this.api.db.createId()),
                        this.newPresentation(row),
                    );
                    touchedZones[row.zoneKey] = true;
                    result.created += 1;
                } else {
                    const presentationKey = await this.mainPresentationKey(row.existingKey);
                    // Solo lo que trae el archivo: una celda vacía deja el valor actual.
                    const presentationData: any = {};
                    if (!row.keepPrice) presentationData.price = row.price;
                    if (!row.keepInventory) presentationData.inventory = row.inventory;
                    if (!row.keepVisible) presentationData.visible = row.visible;
                    if (row.code) presentationData.bar_code = row.code;
                    if (this.auth.countryCode === 'DO' && !row.keepInventory) {
                        presentationData.inventory_ZONE1 = row.inventory;
                        presentationData.inventory_ZONE2 = 0;
                    }
                    const presentationRef = firestore.collection(`bricks/${row.existingKey}/presentations`).doc(presentationKey || this.api.db.createId());
                    if (presentationKey) {
                        if (Object.keys(presentationData).length > 0) batch.update(presentationRef, presentationData);
                    } else {
                        batch.set(presentationRef, { ...this.newPresentation(row), ...presentationData });
                    }

                    const brickData: any = {
                        update_at: now,
                        imported_at: now,
                        imported_by: by,
                        import_source: ctx.source,
                        import_file: ctx.fileName || '',
                    };
                    if (!row.keepVisible) brickData.visible = row.visible;
                    if (row.supplier) brickData.supplier = row.supplier;
                    if (row.section) brickData.section = row.section;
                    if (row.code) {
                        brickData.system_code = row.code;
                        brickData.bar_code = [row.code];
                    }
                    batch.update(firestore.collection('bricks').doc(row.existingKey), brickData);
                    batch.set(firestore.collection(`bricks/${row.existingKey}/history`).doc(this.api.db.createId()), {
                        date: now,
                        action: 'importUpdateBrick',
                        user: by,
                        newData: { ...brickData, ...presentationData },
                        source: ctx.source,
                        file: ctx.fileName || '',
                    });
                    result.updated += 1;
                }
            }

            await batch.commit();
            done += chunk.length;
            if (onProgress) onProgress(done, rows.length);
        }

        // La zona deja de ser un "logo" en cuanto tiene productos (igual que addBrick).
        const zoneKeys = Object.keys(touchedZones);
        for (const zoneKey of zoneKeys) {
            await this.api.updateDocument('bricks', zoneKey, { logo: false });
        }

        return result;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  COPIAR PRODUCTOS A OTRAS ZONAS
    // ═══════════════════════════════════════════════════════════════════════

    /** Plan de copia (qué se crea, actualiza u omite); la lógica vive en el modelo puro. */
    planCopy(
        products: any[],
        zones: ImportZone[],
        catalogs: { [zoneKey: string]: ZoneCatalog },
        onExisting: CopyExistingPolicy,
    ): CopyPlanItem[] {
        return planCopyPure(products, zones, catalogs, onExisting);
    }

    /** Producto con sus presentaciones, para el formulario de edición. */
    getProductForEdit(brickKey: string): Promise<{ brick: any; presentations: any[] }> {
        return this.readSource(brickKey);
    }

    /** Documento del producto original y todas sus presentaciones. */
    private async readSource(brickKey: string): Promise<{ brick: any; presentations: any[] }> {
        const doc = await this.api.getRef('bricks').doc(brickKey).ref.get();
        if (!doc.exists) throw new Error(`El producto ${brickKey} ya no existe.`);
        const brick = doc.data();
        const presentations: any[] = [];
        const snap = await this.api.getRef(`bricks/${brickKey}/presentations`).ref.get();
        snap.forEach(p => presentations.push({ ...p.data(), $key: p.id }));
        return { brick, presentations };
    }

    /** Campos que no viajan con la copia: identidad, borrado, enlaces y auditoría del original. */
    private static readonly NOT_COPIED = [
        '$key', 'delete', 'deleteDetails', 'dynamicLink', 'update_at', 'account_email_update_log',
        'imported_at', 'imported_by', 'import_source', 'import_file', 'copied_from', 'copied_from_zone', 'copied_at', 'copied_by',
        'products_deleted_at', 'products_deleted_by', 'products_deleted_count',
    ];

    private cloneBrick(source: any, item: CopyPlanItem, position: number, options: CopyOptions, now: Date): any {
        const clone: any = {};
        Object.keys(source).forEach(key => {
            if (EventProductsService.NOT_COPIED.indexOf(key) < 0 && source[key] !== undefined) clone[key] = source[key];
        });
        clone.category = item.zone.key;
        clone.position = position;
        clone.created_at = now;
        clone.account_email_create = this.auth.email || '';
        clone.evento = true;
        clone.event_key = options.eventKey || source.event_key || '';
        clone.multiple_monitor = true;
        clone.visible = options.copyInventory ? source.visible === true : false;
        clone.copied_from = item.product.$key;
        clone.copied_from_zone = options.sourceZone.name;
        clone.copied_at = now;
        clone.copied_by = this.auth.email || '';
        return clone;
    }

    private clonePresentation(source: any, options: CopyOptions): any {
        const clone: any = { ...source };
        delete clone.$key;
        if (!options.copyInventory) {
            clone.inventory = 0;
            clone.visible = false;
            if (this.auth.countryCode === 'DO') {
                clone.inventory_ZONE1 = 0;
                clone.inventory_ZONE2 = 0;
            }
        }
        return clone;
    }

    /**
     * Ejecuta el plan: clona producto + presentaciones (o crea la referencia)
     * en cada zona destino, o actualiza el que ya existía. Un mismo producto
     * se lee una sola vez aunque vaya a varias zonas. `positions` trae cuántas
     * entradas tiene cada destino para que lo copiado quede al final.
     */
    async copyProducts(
        plan: CopyPlanItem[],
        options: CopyOptions,
        positions: { [zoneKey: string]: number },
        onProgress?: (done: number, total: number) => void,
    ): Promise<CopyResult> {
        const items = plan.filter(i => i.action !== 'skip');
        const result: CopyResult = { created: 0, updated: 0, skipped: plan.length - items.length };
        if (items.length === 0) return result;

        const firestore = this.api.db.firestore;
        const now = new Date();
        const by = this.auth.email || '';
        const nextPosition = { ...positions };
        const sources: { [key: string]: { brick: any; presentations: any[] } } = {};
        const touchedZones: { [zoneKey: string]: boolean } = {};

        let batch = firestore.batch();
        let ops = 0;
        let done = 0;
        const flush = async () => {
            if (ops > 0) {
                await batch.commit();
                batch = firestore.batch();
                ops = 0;
            }
        };

        for (const item of items) {
            const product = item.product;
            const isReference = !!(product.reference && product.reference.$key);
            const position = nextPosition[item.zone.key] || 0;
            nextPosition[item.zone.key] = position + 1;

            if (isReference) {
                // Una referencia se copia como otra referencia al mismo producto original.
                batch.set(firestore.collection('bricks').doc(this.api.db.createId()), {
                    type: 'reference',
                    brick: product.$key,
                    category: item.zone.key,
                    visible: options.copyInventory ? product.reference.visible === true : false,
                    countryCode: this.auth.countryCode,
                    storeKey: this.auth.storeKey,
                    storeCode: this.auth.storeCode,
                    position,
                    evento: true,
                    event_key: options.eventKey || '',
                    created_at: now,
                    copied_from_zone: options.sourceZone.name,
                    copied_at: now,
                    copied_by: by,
                });
                ops += 1;
                result.created += 1;
            } else {
                if (!sources[product.$key]) sources[product.$key] = await this.readSource(product.$key);
                const source = sources[product.$key];
                const main = source.presentations.filter(p => p.main === true)[0] || source.presentations[0];

                if (item.action === 'create') {
                    const brickKey = this.api.db.createId();
                    const clone = this.cloneBrick(source.brick, item, position, options, now);
                    clone.$key = brickKey;
                    batch.set(firestore.collection('bricks').doc(brickKey), clone);
                    ops += 1;
                    source.presentations.forEach(presentation => {
                        batch.set(
                            firestore.collection(`bricks/${brickKey}/presentations`).doc(this.api.db.createId()),
                            this.clonePresentation(presentation, options),
                        );
                        ops += 1;
                    });
                    result.created += 1;
                } else {
                    // Ya existe en el destino: se alinean precio, inventario y visibilidad con el origen.
                    const targetKey = await this.mainPresentationKey(item.existingKey);
                    const visible = options.copyInventory ? source.brick.visible === true : false;
                    if (main) {
                        const data: any = { price: main.price, visible };
                        if (options.copyInventory) {
                            data.inventory = main.inventory;
                            if (this.auth.countryCode === 'DO') {
                                data.inventory_ZONE1 = main.inventory_ZONE1 || 0;
                                data.inventory_ZONE2 = main.inventory_ZONE2 || 0;
                            }
                        }
                        if (main.undiscounted_price !== undefined) data.undiscounted_price = main.undiscounted_price;
                        if (main.discountPorcentage !== undefined) data.discountPorcentage = main.discountPorcentage;
                        const ref = firestore.collection(`bricks/${item.existingKey}/presentations`).doc(targetKey || this.api.db.createId());
                        if (targetKey) batch.update(ref, data);
                        else batch.set(ref, { ...this.clonePresentation(main, options), ...data });
                        ops += 1;
                    }
                    batch.update(firestore.collection('bricks').doc(item.existingKey), {
                        visible, update_at: now, copied_from: product.$key, copied_from_zone: options.sourceZone.name, copied_at: now, copied_by: by,
                    });
                    batch.set(firestore.collection(`bricks/${item.existingKey}/history`).doc(this.api.db.createId()), {
                        date: now, action: 'copyUpdateBrick', user: by, source: product.$key, sourceZone: options.sourceZone.name,
                    });
                    ops += 2;
                    result.updated += 1;
                }
            }

            touchedZones[item.zone.key] = true;
            done += 1;
            if (ops >= this.OPS_PER_BATCH) await flush();
            if (onProgress) onProgress(done, items.length);
        }
        await flush();

        const zoneKeys = Object.keys(touchedZones);
        for (const zoneKey of zoneKeys) {
            await this.api.updateDocument('bricks', zoneKey, { logo: false });
        }
        return result;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ALTA DE UN PRODUCTO EN UNA BARRA
    // ═══════════════════════════════════════════════════════════════════════

    /** Las presentaciones de entradas se marcan igual que en el formulario de tienda. */
    private static isTicket(presentationName: string): boolean {
        return /E-TICKET/i.test(String(presentationName || ''));
    }

    /**
     * Sube la imagen (data URL) a la misma ruta que usa addBrick y devuelve
     * su URL de descarga. El nombre del archivo es un id aleatorio para no
     * pisar nada.
     */
    async uploadProductImage(zoneKey: string, brickKey: string, dataUrl: string): Promise<string> {
        const ref = firebase.storage().ref().child(`bricks/${zoneKey}/${brickKey}/${this.api.db.createId()}.png`);
        await ref.putString(dataUrl, firebase.storage.StringFormat.DATA_URL);
        return String(await ref.getDownloadURL());
    }

    /**
     * Crea el producto con sus presentaciones en un solo lote. Los campos
     * que el formulario de tienda exige (marca, línea, matriz…) se rellenan
     * con lo mismo que usa el importador, para que nada aguas abajo los eche
     * en falta. La imagen puede venir como archivo (se sube), como URL de
     * otro producto (se comparte, igual que hace la copia entre zonas) o no
     * venir (botella genérica).
     */
    async createProduct(input: NewEventProduct, imageDataUrl: string | null): Promise<CreatedEventProduct> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const brickKey = this.api.db.createId();
        const name = String(input.name || '').trim();
        const supplier = String(input.supplier || '').trim();
        const code = String(input.code || '').trim();
        const section = cleanSectionName(input.section);
        const variants = parseVariants(input.variants);

        let imageUrl = String(input.imageUrl || '').trim();
        if (imageDataUrl) imageUrl = await this.uploadProductImage(input.zoneKey, brickKey, imageDataUrl);
        const hasOwnImage = !!imageUrl;
        if (!imageUrl) imageUrl = DEFAULT_BRICK_IMAGE;

        const isTicket = input.presentations.some(p => EventProductsService.isTicket(p.name));

        const brick: any = {
            $key: brickKey,
            name,
            brand: supplier || 'Evento',
            line: 'Evento',
            description: String(input.description || '').trim() || ' ',
            matriz: name,
            visible: input.visible !== false,
            category: input.zoneKey,
            type: 'brick',
            free_delivery: isTicket,
            on_trade: false,
            exclusiveIcon: false,
            url_link: '',
            supplier,
            nivel: '',
            system_code: code,
            position: Number(input.position) || 0,
            countryCode: this.auth.countryCode,
            storeCode: this.auth.storeCode,
            storeKey: this.auth.storeKey,
            boolAlcohol: false,
            detalles: {
                boolRegion: false, boolTemperatura: false, boolMaridaje: false, boolCristaleria: false, boolNotasCata: false,
            },
            activarVino: false,
            evento: true,
            event_key: input.eventKey,
            multiple_monitor: true,
            image: { downloadURL: imageUrl },
            created_at: now,
            account_email_create: this.auth.email || '',
            created_from: 'event_zone_form',
        };
        if (code) brick.bar_code = [code];
        if (section) brick.section = section;
        brick.kitchen = input.kitchen === true;
        if (variants.length > 0) brick.variants = variants;
        if (isTicket) brick.event_ETicket = true;
        if (hasOwnImage) brick.imagen_carrusel = { downloadURL: [imageUrl] };
        if (input.imageFrom && !imageDataUrl) brick.image_copied_from = input.imageFrom;

        const batch = firestore.batch();
        batch.set(firestore.collection('bricks').doc(brickKey), brick);
        input.presentations.forEach(p => {
            const presentation: any = {
                presentation: String(p.name || '').trim(),
                inventory: Number(p.inventory) || 0,
                price: Number(p.price) || 0,
                bar_code: code,
                visible: p.visible !== false,
                main: p.main === true,
            };
            if (this.auth.countryCode === 'DO') {
                presentation.inventory_ZONE1 = presentation.inventory;
                presentation.inventory_ZONE2 = 0;
            }
            batch.set(firestore.collection(`bricks/${brickKey}/presentations`).doc(this.api.db.createId()), presentation);
        });
        await batch.commit();

        // La zona deja de ser un "logo" en cuanto tiene productos (igual que addBrick).
        await this.api.updateDocument('bricks', input.zoneKey, { logo: false });

        return { key: brickKey, name, imageUrl };
    }

    /**
     * Guarda la edición de un producto: datos, foto y presentaciones (las
     * existentes se actualizan, las nuevas se crean, las quitadas se borran)
     * en un solo lote, con la misma entrada de historial que dejaba el
     * formulario anterior.
     */
    async updateProduct(input: EditEventProduct, imageDataUrl: string | null): Promise<CreatedEventProduct> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const by = this.auth.email || '';
        const name = String(input.name || '').trim();
        const supplier = String(input.supplier || '').trim();
        const code = String(input.code || '').trim();
        const section = cleanSectionName(input.section);
        const isTicket = input.presentations.some(p => EventProductsService.isTicket(p.name));

        const brick: any = {
            name,
            matriz: name,
            supplier,
            brand: supplier || 'Evento',
            description: String(input.description || '').trim() || ' ',
            system_code: code,
            bar_code: code ? [code] : null,
            section,
            kitchen: input.kitchen === true,
            // Lista siempre: sin variantes queda vacía y la caja deja de preguntar.
            variants: parseVariants(input.variants),
            visible: input.visible !== false,
            free_delivery: isTicket,
            event_ETicket: isTicket,
            update_at: now,
            account_email_update: by,
        };

        let imageUrl = '';
        if (input.image === 'upload' && imageDataUrl) {
            imageUrl = await this.uploadProductImage(input.zoneKey, input.key, imageDataUrl);
        } else if (input.image === 'pick') {
            imageUrl = String(input.imageUrl || '').trim();
        } else if (input.image === 'clear') {
            imageUrl = DEFAULT_BRICK_IMAGE;
        }
        if (imageUrl) {
            brick.image = { downloadURL: imageUrl };
            brick.imagen_carrusel = imageUrl === DEFAULT_BRICK_IMAGE ? { downloadURL: [] } : { downloadURL: [imageUrl] };
            brick.image_copied_from = input.image === 'pick' ? (input.imageFrom || '') : '';
        }

        const batch = firestore.batch();
        const brickRef = firestore.collection('bricks').doc(input.key);
        batch.update(brickRef, brick);

        input.presentations.forEach(p => {
            const data: any = {
                presentation: String(p.name || '').trim(),
                inventory: Number(p.inventory) || 0,
                price: Number(p.price) || 0,
                bar_code: code,
                visible: p.visible !== false,
                main: p.main === true,
            };
            if (this.auth.countryCode === 'DO') {
                data.inventory_ZONE1 = data.inventory;
                data.inventory_ZONE2 = 0;
            }
            const ref = firestore.collection(`bricks/${input.key}/presentations`).doc(p.key || this.api.db.createId());
            if (p.key) batch.update(ref, data);
            else batch.set(ref, data);
        });
        (input.removedPresentationKeys || []).forEach(key => {
            batch.delete(firestore.collection(`bricks/${input.key}/presentations`).doc(key));
        });
        batch.set(firestore.collection(`bricks/${input.key}/history`).doc(this.api.db.createId()), {
            date: now,
            action: 'modificationBrick',
            user: by,
            newData: brick,
            presentationData: input.presentations,
            removedPresentations: input.removedPresentationKeys || [],
        });
        await batch.commit();

        return { key: input.key, name, imageUrl };
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  SECCIONES (cómo agrupa la caja)
    // ═══════════════════════════════════════════════════════════════════════

    /** Secciones ya usadas por los productos vivos del evento, sin repetir. */
    async getEventSections(eventKey: string): Promise<string[]> {
        if (!eventKey) return [];
        const snap = await this.api.getRef('bricks').ref
            .where('event_key', '==', eventKey)
            .where('type', '==', 'brick')
            .get();
        const names: string[] = [];
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete !== true && data.section) names.push(String(data.section));
        });
        return mergeSectionNames(names).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    }

    /**
     * Sección de cada producto propio de la zona, por llave. La tabla de la
     * zona recibe los productos de un servicio que no trae este campo.
     */
    async getZoneSectionMap(zoneKey: string): Promise<{ [brickKey: string]: string }> {
        const snap = await this.api.getRef('bricks').ref
            .where('category', '==', zoneKey)
            .where('type', '==', 'brick')
            .get();
        const map: { [brickKey: string]: string } = {};
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete !== true) map[doc.id] = cleanSectionName(data.section);
        });
        return map;
    }

    /** Cambia (o quita, con vacío) la sección de un producto; la caja lo ve en vivo. */
    async setProductSection(brickKey: string, section: string): Promise<string> {
        const name = cleanSectionName(section);
        await this.api.updateDocument('bricks', brickKey, { section: name, update_at: new Date() });
        return name;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  IMÁGENES YA USADAS POR OTROS PRODUCTOS
    // ═══════════════════════════════════════════════════════════════════════

    /** Agrupa productos por URL de imagen; la botella genérica no cuenta. */
    private groupImages(products: { key: string; name: string; url: string }[]): ProductImageOption[] {
        const byUrl: { [url: string]: ProductImageOption } = {};
        const order: string[] = [];
        products.forEach(p => {
            const url = String(p.url || '').trim();
            if (!url || url === DEFAULT_BRICK_IMAGE || !/^https?:\/\//i.test(url)) return;
            const name = String(p.name || '').trim() || 'Sin nombre';
            if (!byUrl[url]) {
                byUrl[url] = { url, key: p.key, name, names: [name], count: 0 };
                order.push(url);
            } else if (!byUrl[url].names.some(n => normalizeText(n) === normalizeText(name))) {
                byUrl[url].names.push(name);
            }
            byUrl[url].count += 1;
        });
        return order.map(url => byUrl[url]).sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    }

    /** Imágenes de los productos vivos de todo el evento (todas las barras). */
    async getEventProductImages(eventKey: string): Promise<ProductImageOption[]> {
        if (!eventKey) return [];
        const snap = await this.api.getRef('bricks').ref
            .where('event_key', '==', eventKey)
            .where('type', '==', 'brick')
            .get();
        const products: { key: string; name: string; url: string }[] = [];
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            products.push({ key: doc.id, name: data.name, url: data.image && data.image.downloadURL });
        });
        return this.groupImages(products);
    }

    /**
     * Busca por nombre en todos los productos de la tienda (Algolia, el mismo
     * índice del buscador del monitor): productos de otros eventos y del
     * catálogo normal que ya tienen foto.
     */
    async searchStoreProductImages(query: string): Promise<ProductImageOption[]> {
        const term = String(query || '').trim();
        if (term.length < 2) return [];
        const client = algoliasearch(algoliaConfig.appId, algoliaConfig.apiKey);
        const index = client.initIndex('bricks');
        const response: any = await index.search(term, { hitsPerPage: 100 });
        const hits: any[] = response && Array.isArray(response.hits) ? response.hits : [];
        const products = hits
            .filter(hit => hit && hit.countryCode == this.auth.countryCode && hit.storeKey == this.auth.storeKey
                && hit.delete !== true && (hit.type === 'brick' || hit.type === undefined))
            .map(hit => ({ key: String(hit.$key || hit.objectID || ''), name: hit.name, url: hit.image && hit.image.downloadURL }));
        return this.groupImages(products);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ELIMINAR TODOS LOS PRODUCTOS DE LA ZONA
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Eliminación lógica de todos los productos listados en la zona, con la
     * misma marca que deja el borrado individual (delete + deleteDetails +
     * visible: false). Un producto propio arrastra sus referencias en otras
     * categorías; una referencia solo se quita de esta zona, el producto
     * original queda intacto. Devuelve cuántos documentos se marcaron.
     */
    async deleteZoneProducts(
        products: any[],
        zoneKey: string,
        onProgress?: (done: number, total: number) => void,
    ): Promise<number> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const deleteDetails = {
            deletedBy: this.auth.token,
            deletedUser: this.auth.email || '',
            date: now,
            reason: 'Productos de la zona eliminados desde la configuración del evento',
        };
        const mark = { delete: true, deleteDetails, visible: false };

        // Primero se reúnen todas las llaves a marcar (producto + referencias).
        const keys: string[] = [];
        for (const product of products) {
            if (product.reference && product.reference.$key) {
                keys.push(product.reference.$key);
                continue;
            }
            if (!product.$key) continue;
            keys.push(product.$key);
            const refs = await this.api.getRef('bricks').ref
                .where('brick', '==', product.$key)
                .where('type', '==', 'reference')
                .get();
            refs.forEach(doc => {
                if (doc.data().delete !== true) keys.push(doc.id);
            });
        }

        const unique = keys.filter((k, i) => keys.indexOf(k) === i);
        for (let i = 0; i < unique.length; i += this.DELETES_PER_BATCH) {
            const batch = firestore.batch();
            unique.slice(i, i + this.DELETES_PER_BATCH).forEach(key => {
                batch.update(firestore.collection('bricks').doc(key), mark);
            });
            await batch.commit();
            if (onProgress) onProgress(Math.min(i + this.DELETES_PER_BATCH, unique.length), unique.length);
        }

        await this.api.updateDocument('bricks', zoneKey, {
            products_deleted_at: now,
            products_deleted_by: this.auth.email || '',
            products_deleted_count: products.length,
        });

        return unique.length;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  INVENTARIO Y PRECIOS DE TODO EL EVENTO (rol 11)
    //  Lectura completa, edición en lote y traslados entre zonas. Las reglas
    //  puras (filtros, cambios, cantidades) viven en shared/event-inventory.
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Todos los productos vivos del evento con sus presentaciones, y las
     * zonas de primer nivel. Una lectura del evento más una por producto
     * (las presentaciones son subcolección). Las referencias no entran: su
     * inventario es el del producto original, que puede estar en otra parte.
     */
    async getEventInventory(eventKey: string): Promise<EventInventory> {
        const result: EventInventory = { zones: [], products: [] };
        if (!eventKey) return result;
        const snap = await this.api.getRef('bricks').ref.where('event_key', '==', eventKey).get();
        const docs: { [key: string]: any } = {};
        snap.forEach(doc => {
            const data = doc.data();
            if (data.delete === true) return;
            docs[doc.id] = { ...data, $key: doc.id };
        });
        const { zones, zoneOf } = this.zoneResolver(docs);
        result.zones = zones;
        const zoneName: { [key: string]: string } = {};
        zones.forEach(z => zoneName[z.key] = z.name);

        const bricks = Object.keys(docs)
            .map(k => docs[k])
            .filter(d => (d.type === 'brick' || d.type === 'club') && !d.hasOwnProperty('table_code'));

        const products = await Promise.all(bricks.map(async d => {
            let raw: any[] = [];
            try {
                raw = (await this.api.getAllDocuments(`bricks/${d.$key}/presentations`)) || [];
            } catch (e) {
                raw = [];
            }
            const presentations: InventoryPresentation[] = raw.map(p => ({
                key: String(p.$key || ''),
                name: String(p.presentation || '').trim(),
                price: Number(p.price) || 0,
                inventory: Number(p.inventory) || 0,
                visible: p.visible !== false,
                main: p.main === true,
            })).sort((a, b) => (b.main ? 1 : 0) - (a.main ? 1 : 0) || a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
            const zoneKey = zoneOf(d.category);
            const product: InventoryProduct = {
                key: d.$key,
                name: String(d.name || '').trim(),
                zoneKey,
                zoneName: zoneName[zoneKey] || String((docs[d.category] && docs[d.category].name) || 'Sin zona'),
                categoryKey: String(d.category || ''),
                section: cleanSectionName(d.section),
                code: String(d.system_code || (Array.isArray(d.bar_code) ? d.bar_code[0] : '') || '').trim(),
                supplier: String(d.supplier || '').trim(),
                image: d.image && d.image.downloadURL ? String(d.image.downloadURL) : '',
                visible: d.visible !== false,
                kitchen: d.kitchen === true,
                variants: parseVariants(d.variants),
                presentations,
            };
            return product;
        }));

        const zoneIndex: { [key: string]: number } = {};
        zones.forEach((z, i) => zoneIndex[z.key] = i);
        result.products = products
            .filter(p => p.presentations.length > 0)
            .sort((a, b) => {
                const za = zoneIndex[a.zoneKey] === undefined ? 999 : zoneIndex[a.zoneKey];
                const zb = zoneIndex[b.zoneKey] === undefined ? 999 : zoneIndex[b.zoneKey];
                return za - zb || a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
            });
        return result;
    }

    /** Campos de inventario de una presentación (RD guarda además el inventario por zona de tienda). */
    private inventoryFields(inventory: number): any {
        const data: any = { inventory };
        if (this.auth.countryCode === 'DO') {
            data.inventory_ZONE1 = inventory;
            data.inventory_ZONE2 = 0;
        }
        return data;
    }

    /**
     * Guarda los cambios de precio, inventario y visibilidad de la vista
     * general en un solo lote: solo los campos que cambiaron (un precio
     * corregido no pisa las existencias que la caja descontó mientras
     * tanto). `productVisible` trae, por producto, cómo queda su `visible`
     * cuando alguna de sus presentaciones cambió. Cada producto recibe una
     * entrada de historial con lo que cambió.
     */
    async saveInventoryEdits(edits: InventoryEdit[], productVisible: { [brickKey: string]: boolean }): Promise<number> {
        if (edits.length === 0) return 0;
        const firestore = this.api.db.firestore;
        const now = new Date();
        const by = this.auth.email || '';
        const byBrick: { [brickKey: string]: InventoryEdit[] } = {};
        edits.forEach(edit => (byBrick[edit.brickKey] = byBrick[edit.brickKey] || []).push(edit));

        const brickKeys = Object.keys(byBrick);
        for (let i = 0; i < brickKeys.length; i += this.ROWS_PER_BATCH) {
            const batch = firestore.batch();
            brickKeys.slice(i, i + this.ROWS_PER_BATCH).forEach(brickKey => {
                const changes: any[] = [];
                byBrick[brickKey].forEach(edit => {
                    const data: any = {};
                    if (edit.price !== undefined) data.price = edit.price;
                    if (edit.inventory !== undefined) Object.assign(data, this.inventoryFields(edit.inventory));
                    if (edit.visible !== undefined) data.visible = edit.visible;
                    batch.update(firestore.collection(`bricks/${brickKey}/presentations`).doc(edit.presentationKey), data);
                    changes.push({
                        presentation: edit.presentationName,
                        before: edit.before,
                        after: {
                            price: edit.price !== undefined ? edit.price : edit.before.price,
                            inventory: edit.inventory !== undefined ? edit.inventory : edit.before.inventory,
                            visible: edit.visible !== undefined ? edit.visible : edit.before.visible,
                        },
                    });
                });
                const brickData: any = { update_at: now, account_email_update: by };
                if (productVisible[brickKey] !== undefined) brickData.visible = productVisible[brickKey];
                batch.update(firestore.collection('bricks').doc(brickKey), brickData);
                batch.set(firestore.collection(`bricks/${brickKey}/history`).doc(this.api.db.createId()), {
                    date: now,
                    action: 'inventoryOverview',
                    user: by,
                    newData: brickData,
                    changes,
                });
            });
            await batch.commit();
        }
        // La caja escucha las presentaciones en vivo; la caché de documentos del monitor se invalida.
        brickKeys.forEach(key => this.api.forgetDocument('bricks', key));
        return edits.length;
    }

    /**
     * Copia del producto en la zona destino para poder trasladarle
     * inventario: nace con existencias 0 y oculta (el traslado la enciende).
     * Devuelve la llave del producto nuevo y la de la presentación que
     * corresponde a la de origen.
     */
    private async createTransferCopy(source: InventoryProduct, presentationName: string, zone: InventoryZone, eventKey: string): Promise<{ brickKey: string; presentationKey: string }> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const read = await this.readSource(source.key);
        const catalog = await this.getZoneCatalog(zone.key);
        const item: CopyPlanItem = { product: { $key: source.key, name: source.name }, zone: { key: zone.key, name: zone.name }, action: 'create', existingKey: '', note: '' };
        const options: CopyOptions = { sourceZone: { key: source.categoryKey, name: source.zoneName }, eventKey, copyInventory: false };

        const brickKey = this.api.db.createId();
        const clone = this.cloneBrick(read.brick, item, catalog.count, options, now);
        clone.$key = brickKey;
        clone.transfer_created = true;
        const batch = firestore.batch();
        batch.set(firestore.collection('bricks').doc(brickKey), clone);
        let presentationKey = '';
        const target = normalizeText(presentationName);
        const main = read.presentations.filter(p => p.main === true)[0] || read.presentations[0];
        read.presentations.forEach(presentation => {
            const key = this.api.db.createId();
            const same = normalizeText(presentation.presentation) === target;
            if (same || (!presentationKey && presentation === main)) presentationKey = key;
            batch.set(firestore.collection(`bricks/${brickKey}/presentations`).doc(key), this.clonePresentation(presentation, options));
        });
        await batch.commit();
        await this.api.updateDocument('bricks', zone.key, { logo: false });
        return { brickKey, presentationKey };
    }

    /**
     * Traslada `quantity` unidades de una presentación a la misma
     * presentación del producto homónimo en otra zona; si el destino no
     * tiene el producto, se crea antes una copia. La resta y la suma van en
     * una transacción: se leen las existencias reales al confirmar (la caja
     * puede estar vendiendo) y si ya no alcanzan, el traslado se rechaza sin
     * tocar nada. El destino queda visible (recibió existencias); el origen
     * conserva su visibilidad aunque quede en 0 (la caja lo muestra agotado).
     * Queda historial en ambos productos y un registro del traslado en el
     * evento.
     */
    async transferInventory(request: {
        eventKey: string;
        from: InventoryProduct;
        fromPresentation: InventoryPresentation;
        toZone: InventoryZone;
        to: InventoryProduct | null;
        quantity: number;
    }): Promise<InventoryTransferResult> {
        const firestore = this.api.db.firestore;
        const now = new Date();
        const by = this.auth.email || '';
        const quantity = Math.floor(Number(request.quantity) || 0);
        if (quantity < 1) throw new Error('La cantidad tiene que ser al menos 1.');
        if (request.toZone.key === request.from.zoneKey) throw new Error('La zona destino es la misma de origen.');

        let toBrickKey = request.to ? request.to.key : '';
        let toPresentationKey = '';
        let created = false;
        if (request.to) {
            const match = matchPresentation(request.to, request.fromPresentation.name);
            if (!match) throw new Error(`${request.to.name} no tiene presentaciones en ${request.toZone.name}.`);
            toPresentationKey = match.key;
        } else {
            const copy = await this.createTransferCopy(request.from, request.fromPresentation.name, request.toZone, request.eventKey);
            toBrickKey = copy.brickKey;
            toPresentationKey = copy.presentationKey;
            created = true;
        }

        const fromRef = firestore.collection(`bricks/${request.from.key}/presentations`).doc(request.fromPresentation.key);
        const toRef = firestore.collection(`bricks/${toBrickKey}/presentations`).doc(toPresentationKey);
        const log: InventoryTransferLog = {
            date: now, user: by,
            productName: request.from.name, presentationName: request.fromPresentation.name, quantity,
            fromZoneKey: request.from.zoneKey, fromZoneName: request.from.zoneName,
            toZoneKey: request.toZone.key, toZoneName: request.toZone.name,
            created, fromBefore: 0, fromAfter: 0, toBefore: 0, toAfter: 0,
        };

        await firestore.runTransaction(async tx => {
            const fromSnap = await tx.get(fromRef);
            const toSnap = await tx.get(toRef);
            if (!fromSnap.exists) throw new Error('La presentación de origen ya no existe.');
            if (!toSnap.exists) throw new Error('La presentación de destino ya no existe.');
            const fromBefore = Number((fromSnap.data() || {}).inventory) || 0;
            const toBefore = Number((toSnap.data() || {}).inventory) || 0;
            if (fromBefore < quantity) {
                throw new Error(`Solo quedan ${fromBefore} ${fromBefore === 1 ? 'unidad' : 'unidades'} en ${request.from.zoneName}; refresca y vuelve a intentar.`);
            }
            log.fromBefore = fromBefore;
            log.fromAfter = fromBefore - quantity;
            log.toBefore = toBefore;
            log.toAfter = toBefore + quantity;

            tx.update(fromRef, this.inventoryFields(log.fromAfter));
            tx.update(toRef, { ...this.inventoryFields(log.toAfter), visible: true });
            tx.update(firestore.collection('bricks').doc(toBrickKey), { visible: true, update_at: now, account_email_update: by });
            tx.update(firestore.collection('bricks').doc(request.from.key), { update_at: now, account_email_update: by });

            const entry = {
                date: now, action: 'inventoryTransfer', user: by, quantity,
                presentation: request.fromPresentation.name,
                from: { brick: request.from.key, zone: request.from.zoneKey, zoneName: request.from.zoneName, before: fromBefore, after: log.fromAfter },
                to: { brick: toBrickKey, zone: request.toZone.key, zoneName: request.toZone.name, before: toBefore, after: log.toAfter, created },
            };
            tx.set(firestore.collection(`bricks/${request.from.key}/history`).doc(this.api.db.createId()), entry);
            tx.set(firestore.collection(`bricks/${toBrickKey}/history`).doc(this.api.db.createId()), entry);
            tx.set(firestore.collection(`events/${request.eventKey}/inventory_transfers`).doc(this.api.db.createId()), {
                ...log,
                fromBrick: request.from.key,
                toBrick: toBrickKey,
            });
        });

        this.api.forgetDocument('bricks', request.from.key);
        this.api.forgetDocument('bricks', toBrickKey);
        return { log, fromBrickKey: request.from.key, fromPresentationKey: request.fromPresentation.key, toBrickKey, toPresentationKey };
    }

    /** Últimos traslados del evento, el más reciente primero. */
    async getRecentTransfers(eventKey: string, limit: number = 20): Promise<InventoryTransferLog[]> {
        if (!eventKey) return [];
        const snap = await this.api.getRef(`events/${eventKey}/inventory_transfers`).ref
            .orderBy('date', 'desc')
            .limit(limit)
            .get();
        const list: InventoryTransferLog[] = [];
        snap.forEach(doc => {
            const d = doc.data();
            list.push({
                key: doc.id,
                date: this.toDate(d.date),
                user: String(d.user || ''),
                productName: String(d.productName || ''),
                presentationName: String(d.presentationName || ''),
                quantity: Number(d.quantity) || 0,
                fromZoneKey: String(d.fromZoneKey || ''),
                fromZoneName: String(d.fromZoneName || ''),
                toZoneKey: String(d.toZoneKey || ''),
                toZoneName: String(d.toZoneName || ''),
                created: d.created === true,
                fromBefore: Number(d.fromBefore) || 0,
                fromAfter: Number(d.fromAfter) || 0,
                toBefore: Number(d.toBefore) || 0,
                toAfter: Number(d.toAfter) || 0,
            });
        });
        return list;
    }
}
